// src/utils/personFilterUtils.js
//
// Pure, framework-free predicates for filtering a list of "people" — patients or
// staff/provider accounts — by free-text name/email substrings and a
// "date added" range.
//
// WHY THIS EXISTS
// ----------------
// The patient dashboard (PatientTable) and the staff page (ProviderAccountsTable)
// both filter their lists by the same three things: a name substring, an email
// substring, and a created-at date range. That logic used to live inline in the
// patient table only. Extracting it here means both tables apply *identical*
// matching rules (case-insensitivity, blank-filter = no constraint, Firestore
// Timestamp handling, inclusive day boundaries) and the rules are unit-tested in
// one place rather than re-implemented per table.
//
// Conventions shared with the original inline patient logic:
//   - A blank/empty filter value is treated as "no constraint" (matches everyone).
//   - Matching is case-insensitive substring matching.
//   - Name is matched against the combined "firstName lastName" string, so a
//     query like "jane d" matches "Jane Doe".
//   - A date range only constrains results when BOTH ends are set. A record with
//     a missing/invalid created date is excluded once a range is active (it can't
//     be shown to fall inside the range) — this mirrors the prior patient behaviour
//     where `new Date(undefined)` produced an Invalid Date that failed the compare.

/**
 * Case-insensitive substring test. An empty/blank `needle` imposes no constraint.
 * @param {string|undefined|null} haystack
 * @param {string|undefined|null} needle
 * @returns {boolean}
 */
export function matchesSubstring(haystack, needle) {
  if (!needle || !needle.trim()) return true;
  return (haystack || '').toLowerCase().includes(needle.toLowerCase());
}

/**
 * Matches a person's full name ("firstName lastName") against a substring query.
 * @param {{firstName?: string, lastName?: string}} person
 * @param {string} nameFilter
 */
export function matchesFullName(person, nameFilter) {
  if (!nameFilter || !nameFilter.trim()) return true;
  const fullName = `${person?.firstName || ''} ${person?.lastName || ''}`;
  return fullName.toLowerCase().includes(nameFilter.toLowerCase());
}

/**
 * Matches a person's email against a substring query.
 * @param {{email?: string}} person
 * @param {string} emailFilter
 */
export function matchesEmail(person, emailFilter) {
  return matchesSubstring(person?.email, emailFilter);
}

/**
 * Normalises the various created-at shapes we see (Firestore Timestamp, Date,
 * epoch millis, or an ISO string) to a Date, or null when absent.
 */
function toDate(value) {
  if (value == null) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
}

/**
 * Tests whether a person's "date added" falls within an inclusive date range.
 * The range only constrains results when BOTH `startDate` and `endDate` are set;
 * otherwise every record passes. Day boundaries are inclusive (start at 00:00:00,
 * end at 23:59:59.999) so a record created at any time on the end date still matches.
 *
 * @param {object} person
 * @param {{startDate: Date|null, endDate: Date|null}} dateRange
 * @param {string} [dateField='createdAt'] field on `person` holding the created date
 */
export function matchesDateAddedRange(person, dateRange, dateField = 'createdAt') {
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) return true;

  const created = toDate(person?.[dateField]);
  if (!created || isNaN(created.getTime())) return false;

  const start = new Date(dateRange.startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(dateRange.endDate);
  end.setHours(23, 59, 59, 999);

  return created >= start && created <= end;
}

/**
 * Combined predicate for the staff (provider accounts) filters: name + email +
 * date-added range, all of which must pass (AND). A staff record's created date
 * lives on `createdAt`.
 *
 * @param {object} staff
 * @param {{staffName?: string, emailAddress?: string, dateRange?: {startDate: Date|null, endDate: Date|null}}} filters
 */
export function matchesStaffFilters(staff, filters) {
  if (!filters) return true;
  return (
    matchesFullName(staff, filters.staffName) &&
    matchesEmail(staff, filters.emailAddress) &&
    matchesDateAddedRange(staff, filters.dateRange, 'createdAt')
  );
}

/**
 * True when at least one staff filter is actively constraining results. Used to
 * choose between the "no staff in your org" empty state and the "nothing matched
 * your filters" empty state.
 */
export function hasActiveStaffFilters(filters) {
  if (!filters) return false;
  const nameSet = !!(filters.staffName && filters.staffName.trim());
  const emailSet = !!(filters.emailAddress && filters.emailAddress.trim());
  const dateSet = !!(filters.dateRange && filters.dateRange.startDate && filters.dateRange.endDate);
  return nameSet || emailSet || dateSet;
}
